const DB_NAME = 'placement-cache-db';
const STORE_NAME = 'placement-cache-store';
const DB_VERSION = 1;

/**
 * Open IndexedDB database connection.
 * @returns {Promise<IDBDatabase>} db connection instance.
 */
function openDb() {
  return new Promise((resolve, reject) => {
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Return data from IndexedDB by key.
 * @param {string} key The key to retrieve. 
 * @returns {Promise<any>} data stored under the key.
 */
async function idbGet(key) {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const r = store.get(key);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
  } catch (err) {
    return null;
  }
}

/**
 * Set data based on key in index db.
 * @param {string} key Key to store data.
 * @param {object} value Value to store.
 * @returns {Promise<void>}
 */
async function idbSet(key, value) {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const r = store.put(value, key);
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  } catch (err) {
    return;
  }
}

/**
 * Delete data from IndexedDB by key.
 * @param {string} key Key to be deleted.
 * @returns {Promise<void>}
 */
async function idbDelete(key) {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const r = store.delete(key);
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  } catch (err) {
    return;
  }
}

const memory = new Map();
const inFlight = new Map();

/**
 * Stringify object in stable order.
 * @param {object} obj 
 * @returns {string}
 */
function stableStringify(obj) {
  if (obj === undefined) return 'null';
  if (obj === null) return 'null';
  if (typeof obj !== 'object') return JSON.stringify(obj);
  if (Array.isArray(obj)) return '[' + obj.map(stableStringify).join(',') + ']';
  const keys = Object.keys(obj).sort();
  return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',') + '}';
}

/**
 * PlacementCache module for caching placement data in memory and IndexedDB.
 */
const PlacementCache = {
  /**
   * Get data.
   * @param {string} key key to fetch data.
   * @returns 
   */
  async get(key) {
    const now = Date.now();
    const mem = memory.get(key);
    if (mem && mem.expires > now) return mem.items;
    if (mem) memory.delete(key);

    const entry = await idbGet(key).catch(() => null);
    if (!entry) return null;
    if (entry.expires && entry.expires > now) {
      memory.set(key, { items: entry.items, expires: entry.expires });
      return entry.items;
    }
    await idbDelete(key).catch(() => {});
    return null;
  },
  /**
   * Set data.
   * @param {string} key 
   * @param {object} items 
   * @param {number} ttlMs 
   */
  async set(key, items, ttlMs = 60 * 60 * 1000) {
    const expires = Date.now() + ttlMs;
    memory.set(key, { items, expires });
    await idbSet(key, { items, expires }).catch(() => {});
  },
  /**
   * Get in-flight promise.
   * @param {string} key 
   * @returns {Promise<any>}
   */
  getInFlight(key) {
    return inFlight.get(key);
  },
  /**
   * Set in-flight promise.
   * @param {string} key 
   * @param {Promise<any>} promise 
   */
  setInFlight(key, promise) {
    inFlight.set(key, promise);
  },
  /**
   * Clear in-flight promise.
   * @param {string} key 
   */
  clearInFlight(key) {
    inFlight.delete(key);
  },
  /**
   * Make cache key.
   * @param {String} campaignId - Campaign ID
   * @param {Object} filterObj - Filter object
   * @param {String} versionOneApi - API version
   * @returns {String} Generated cache key
   */
  makeKey(campaignId, filterObj, versionOneApi) {
    const copy = filterObj && typeof filterObj === 'object' ? { ...filterObj } : filterObj;
    if (copy && copy.page_no !== undefined) delete copy.page_no;
    if (copy && copy.page_size !== undefined) delete copy.page_size;
    return stableStringify({ campaignId, filterObj: copy, versionOneApi });
  },
  /**
   * Delete data by key.
   * @param {string} key
   */
  async delete(key) {
    try {
      memory.delete(key);
      await idbDelete(key).catch(() => {});
      inFlight.delete(key);
    } catch (err) {
      console.error('PlacementCache.delete error', err);
    }
  },
  /**
   * Delete entire database.
   */
  async deleteDatabase() {
    try {
      memory.clear();
      inFlight.clear();

      return new Promise((resolve, reject) => {
        try {
          const req = window.indexedDB.deleteDatabase(DB_NAME);
          req.onsuccess = () => resolve();
          req.onblocked = () => {
            console.warn('PlacementCache.deleteDatabase blocked');
            resolve();
          };
          req.onerror = () => reject(req.error);
        } catch (err) {
          console.error('PlacementCache.deleteDatabase error', err);
          resolve();
        }
      });
    } catch (err) {
      console.error('PlacementCache.deleteDatabase outer error', err);
    }
  }
};

export default PlacementCache;
